import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { WORKFLOW, fmtEur, periodLabel } from './reconciliationUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);

const request = async (path, body) => {
  const response = await fetch(`${API}/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hw_token')}` },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'Salvataggio non riuscito');
  return data;
};

export const ManualPaymentDeclarationModal = ({ mode, item, onClose, onSaved }) => {
  const isPayment = mode === 'payment';
  const current = isPayment ? item?.payment_info : item?.declaration_info;
  const [amount, setAmount] = useState(current?.amount ?? (isPayment ? item?.theoretical_due_amount : item?.theoretical_due_amount) ?? '');
  const [date, setDate] = useState((current?.date || today()).slice(0, 10));
  const [reference, setReference] = useState(current?.reference || current?.protocol_code || '');
  const [method, setMethod] = useState(current?.method || 'f24');
  const [portal, setPortal] = useState(current?.portal_name || '');
  const [nights, setNights] = useState(current?.declared_nights ?? '');
  const [guests, setGuests] = useState(current?.declared_guests ?? '');
  const [notes, setNotes] = useState(current?.notes || '');
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (amount === '' || Number(amount) < 0 || !date) {
      toast.error('Importo e data sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      if (isPayment) {
        await request('/tourist-tax/reconciliation/manual-payment', {
          report_id: item.report_id, amount: Number(amount), payment_method: method,
          payment_date: date, payment_reference: reference, notes,
        });
        if (receipt) {
          const form = new FormData();
          form.append('file', receipt);
          const upload = await fetch(`${API}/api/tourist-tax/reports/${item.report_id}/upload-receipt`, {
            method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` }, body: form,
          });
          if (!upload.ok) throw new Error('Versamento salvato, ma caricamento quietanza non riuscito');
        }
      } else {
        await request('/tourist-tax/reconciliation/declaration', {
          report_id: item.report_id, amount: Number(amount), submission_date: date,
          protocol_code: reference, declared_nights: nights === '' ? null : Number(nights),
          declared_guests: guests === '' ? null : Number(guests), portal_name: portal, notes,
        });
      }
      toast.success(isPayment ? 'Versamento registrato e riconciliato' : 'Dichiarazione registrata');
      onSaved();
    } catch (error) { toast.error(error.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto [&>button.absolute]:hidden" data-testid={isPayment ? 'manual-payment-modal' : 'manual-declaration-modal'}>
        <DialogHeader>
          <DialogTitle>{isPayment ? 'Registra versamento' : 'Registra dichiarazione'}</DialogTitle>
        </DialogHeader>
        <button className="absolute right-4 top-4 text-slate-500 hover:text-slate-900" onClick={onClose} data-testid="manual-record-modal-close-btn" aria-label="Chiudi">
          <X className="h-4 w-4" />
        </button>
        <div className="border-l-2 border-sky-500 pl-3 text-sm" data-testid="manual-record-context">
          <strong>{item.property_code} · {item.property_name}</strong>
          <div className="text-slate-500">{item.comune_name} · {periodLabel(item)} · dovuto {fmtEur(item.theoretical_due_amount)}</div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="manual-record-amount">Importo (€)</Label>
            <Input id="manual-record-amount" type="number" min="0" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} data-testid={isPayment ? 'modal-payment-amount-input' : 'modal-decl-amount-input'} />
          </div>
          <div>
            <Label htmlFor="manual-record-date">{isPayment ? 'Data pagamento / valuta' : 'Data invio portale'}</Label>
            <Input id="manual-record-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} data-testid={isPayment ? 'modal-payment-date-input' : 'modal-decl-date-input'} />
          </div>
          {isPayment ? (
            <div>
              <Label>Metodo</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="modal-payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="f24">F24</SelectItem><SelectItem value="pagopa">PagoPA</SelectItem>
                  <SelectItem value="sepa">Bonifico SEPA</SelectItem><SelectItem value="other">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label htmlFor="manual-decl-portal">Portale comunale</Label>
              <Input id="manual-decl-portal" value={portal} onChange={(event) => setPortal(event.target.value)} placeholder="TouristTax, StayTour…" data-testid="modal-decl-portal-input" />
            </div>
          )}
          <div>
            <Label htmlFor="manual-record-reference">{isPayment ? 'CRO / IUV / quietanza' : 'Protocollo / ricevuta'}</Label>
            <Input id="manual-record-reference" value={reference} onChange={(event) => setReference(event.target.value)} data-testid={isPayment ? 'modal-payment-ref-input' : 'modal-decl-protocol-input'} />
          </div>
          {!isPayment && <>
            <div><Label htmlFor="decl-nights">Notti dichiarate</Label><Input id="decl-nights" type="number" min="0" value={nights} onChange={(event) => setNights(event.target.value)} data-testid="modal-decl-nights-input" /></div>
            <div><Label htmlFor="decl-guests">Persone dichiarate</Label><Input id="decl-guests" type="number" min="0" value={guests} onChange={(event) => setGuests(event.target.value)} data-testid="modal-decl-guests-input" /></div>
          </>}
        </div>
        {isPayment && (
          <div>
            <Label htmlFor="payment-receipt">Contabile / quietanza (facoltativa)</Label>
            <label htmlFor="payment-receipt" className="mt-1 flex cursor-pointer items-center gap-2 border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-sm text-slate-600 hover:border-sky-400" data-testid="modal-payment-receipt-label">
              <Upload className="h-4 w-4" /> {receipt ? receipt.name : 'Seleziona PDF, PNG o JPG'}
            </label>
            <input id="payment-receipt" className="sr-only" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(event) => setReceipt(event.target.files?.[0] || null)} data-testid="modal-payment-receipt-input" />
          </div>
        )}
        <div><Label htmlFor="manual-record-notes">Note audit</Label><Textarea id="manual-record-notes" value={notes} onChange={(event) => setNotes(event.target.value)} data-testid="manual-record-notes-input" /></div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="manual-record-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-700 text-white hover:bg-sky-800" data-testid={isPayment ? 'modal-payment-save-btn' : 'modal-decl-save-btn'}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva e riconcilia
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const WorkflowStatusModal = ({ item, onClose, onSaved }) => {
  const [status, setStatus] = useState(item.workflow_status || 'to_verify');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => setReason(item.resolution_note || ''), [item]);
  const save = async () => {
    if (status === 'anomaly_resolved' && !reason.trim()) { toast.error('Inserisci la motivazione della risoluzione'); return; }
    setSaving(true);
    try {
      await request('/tourist-tax/reconciliation/workflow-status', { report_id: item.report_id, new_status: status, reason_note: reason });
      toast.success('Stato operativo aggiornato'); onSaved();
    } catch (error) { toast.error(error.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md [&>button.absolute]:hidden" data-testid="workflow-status-modal">
        <DialogHeader><DialogTitle>Aggiorna stato operativo</DialogTitle></DialogHeader>
        <button className="absolute right-4 top-4" onClick={onClose} data-testid="workflow-status-close-btn" aria-label="Chiudi"><X className="h-4 w-4" /></button>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="workflow-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(WORKFLOW).map(([value, config]) => <SelectItem key={value} value={value}>{config.label}</SelectItem>)}</SelectContent>
        </Select>
        <div><Label htmlFor="status-reason">Motivazione / nota audit</Label><Textarea id="status-reason" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Obbligatoria per chiudere un’anomalia" data-testid="modal-status-reason-input" /></div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose} data-testid="workflow-status-cancel-btn">Annulla</Button><Button onClick={save} disabled={saving} data-testid="modal-status-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva stato</Button></div>
      </DialogContent>
    </Dialog>
  );
};